import React from 'react';
import { ModelInfo, UsageByModel } from './types';

interface UsageCardProps {
  usage: UsageByModel[] | null;
  models: ModelInfo[];
}

function estimateCost(row: UsageByModel, models: ModelInfo[]): number | null {
  const m = models.find(x => x.id === row.model);
  if (!m || m.inPerM == null || m.outPerM == null) return null;
  return (Number(row.input_tokens) / 1e6) * m.inPerM + (Number(row.output_tokens) / 1e6) * m.outPerM;
}

// This month's AI usage from the app's own logs, with a rough cost estimate.
const UsageCard: React.FC<UsageCardProps> = ({ usage, models }) => {
  const rows = (usage || []).filter(r => r.calls > 0);
  const totalCost = rows.reduce((sum, r) => sum + (estimateCost(r, models) ?? 0), 0);

  return (
    <section className="settings-card">
      <h3>This Month's AI Usage</h3>
      {rows.length === 0 ? (
        <p className="settings-hint">No AI usage recorded this month.</p>
      ) : (
        <>
          <table className="settings-usage-table">
            <thead>
              <tr><th>Model</th><th>Requests</th><th>Input tokens</th><th>Output tokens</th><th>Est. cost</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const cost = estimateCost(r, models);
                const label = models.find(m => m.id === r.model)?.label || r.model || 'Unknown';
                return (
                  <tr key={i}>
                    <td>{label}</td>
                    <td>{r.calls}</td>
                    <td>{Number(r.input_tokens).toLocaleString()}</td>
                    <td>{Number(r.output_tokens).toLocaleString()}</td>
                    <td>{cost == null ? '—' : `$${cost.toFixed(2)}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="settings-usage-total">Estimated total: <strong>${totalCost.toFixed(2)}</strong></p>
        </>
      )}
      <p className="settings-hint">
        Estimates only — your{' '}
        <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">Anthropic console</a>{' '}
        is the authoritative record of spend.
      </p>
    </section>
  );
};

export default UsageCard;
